import type { MediasSection } from "@/modules/corporate/types/sections"
import type { FunctionComponent } from "react"

import dynamic from "next/dynamic"
import Image from "next/image"
import { useState } from "react"

import { Layout } from "@jobteaser/ui-kit/components/Layout/Layout"

import { Title } from "@/modules/corporate/components/Sections/Title/Title"
import { useCorporateTrackingClick } from "@/modules/corporate/tracking/useCorporateTrackingClick"
import { RichText } from "@/modules/kontent/components/RichText"

import styles from "./Medias.module.css"

export type MediasPropsType = {
  section: MediasSection
}

const DynamicVideoModal = dynamic(() => import("./VideoModal").then(mod => mod.VideoModal))

export const Medias: FunctionComponent<MediasPropsType> = ({ section: { title, medias } }) => {
  const [isModalRegularOpen, setIsModalRegularOpen] = useState<boolean>(false)
  const [currentUrl, setCurrentUrl] = useState<string>("")
  const trackClickEvent = useCorporateTrackingClick()

  const onTrackModalEvent = (): void => {
    trackClickEvent("corporate_media_modal", {
      visual_context: isModalRegularOpen ? "close" : "open",
    })
  }

  const onTrackMediaEvent = (mediaUrl: string): void => {
    trackClickEvent("corporate_media_video", {
      clicked_url: mediaUrl,
    })
    onTrackModalEvent()
  }

  return (
    <Layout hasTopPadding hasBottomPadding>
      <Layout.Column>
        {!!title && <Title title={title} tag="h2" isCentered />}
        <div className={styles.list}>
          {medias.map(media => {
            const image1 = media.videoImage1.length ? media.videoImage1[0] : null
            const image1Ratio = image1 && (image1.width || 0) < (image1.height || 0) ? "portrait" : "landscape"
            const image2 = media.videoImage2.length ? media.videoImage2[0] : null
            const image2Ratio = image2 && (image2.width || 0) < (image2.height || 0) ? "portrait" : "landscape"

            return (
              <div
                key={JSON.stringify(media.summary)}
                className={styles.summary}
                data-format={image2 ? `${image1Ratio}-${image2Ratio}` : image1Ratio}
              >
                <RichText
                  value={media.summary}
                  wrapperProps={{
                    className: styles.text,
                    resetSpacing: true,
                    tag: "div",
                  }}
                />
                <div className={styles.description}>
                  <RichText
                    value={media.description}
                    wrapperProps={{
                      className: styles.quote,
                      resetSpacing: true,
                      tag: "div",
                    }}
                  />
                </div>
                {!!media.videoImage1.length && (
                  <picture className={styles.figure1}>
                    {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
                    <Image
                      src={media.videoImage1[0].url}
                      className={styles.image1}
                      alt={media.videoImage1[0].description || ""}
                      onClick={() => {
                        setIsModalRegularOpen(true)
                        setCurrentUrl(media.videoUrl1)
                        onTrackMediaEvent(media.videoUrl1)
                      }}
                      height={media.videoImage1[0].height || undefined}
                      width={media.videoImage1[0].width || undefined}
                      sizes="(min-width: 64rem) 50vw, 100vw"
                    />
                  </picture>
                )}
                {!!media.videoImage2.length && (
                  <picture className={styles.figure2}>
                    {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
                    <Image
                      src={media.videoImage2[0].url}
                      className={styles.image2}
                      alt={media.videoImage2[0].description || ""}
                      onClick={() => {
                        setIsModalRegularOpen(true)
                        setCurrentUrl(media.videoUrl2)
                        onTrackMediaEvent(media.videoUrl2)
                      }}
                      height={media.videoImage2[0].height || undefined}
                      width={media.videoImage2[0].width || undefined}
                      sizes="(min-width: 64rem) 50vw, 100vw"
                    />
                  </picture>
                )}

                {isModalRegularOpen && currentUrl && (
                  <DynamicVideoModal
                    url={currentUrl}
                    onClose={() => {
                      setIsModalRegularOpen(false)
                      onTrackModalEvent()
                    }}
                  />
                )}
              </div>
            )
          })}
        </div>
      </Layout.Column>
    </Layout>
  )
}
